import { produce } from 'immer';
import { isEmptyObject } from 'utils/misc';
import type {
  TreeNode,
  DataMap,
  SelectionTreeNode,
  SelectionMap,
} from 'state/filterToolbar';

/**
 * Recursively filters leaf nodes not included in allowedIds.
 * @param tree - The tree structure to filter.
 * @param categoryIds - Allowed category IDs.
 * @param statusIds - Allowed status IDs.
 * @returns A new tree structure with only the allowed leaf nodes.
 */
export function filterLeafNodesByCategoriesAndStatus(
  tree: DataMap,
  categoryIds: (string | number)[],
  statusIds: (string | number)[],
) {
  return produce(tree, (draft) => {
    const recurse = (branch: DataMap, context: 'categories' | 'statuses' | null) => {
      Object.keys(branch).forEach((key) => {
        const node = branch[key];

        if (node.children && Object.keys(node.children).length > 0) {
          // Determine context for deeper recursion
          let nextContext: 'categories' | 'statuses' | null = context;
          if (String(key).startsWith('categories-')) {
            nextContext = 'categories';
          }
          if (String(key).startsWith('statuses-')) {
            nextContext = 'statuses';
          }

          recurse(node.children, nextContext);
        } else if (
          (context === 'categories' && !categoryIds.includes(node.value)) ||
          (context === 'statuses' && !statusIds.includes(node.value))
        ) {
          delete branch[key];
        }
      });
    };

    recurse(draft, null);
  });
}

/**
 * Recursively marks nodes in selectedOptions as isSelected or isPartialSelected.
 * @param options - The full options tree.
 * @param selected - The selected options tree to mark.
 * @returns A new selected options tree with selection states marked.
 */
export function markSelectionStates(options: DataMap, selected: SelectionMap) {
  return produce(selected, (draft) => {
    const traverse = (opt: DataMap, sel: SelectionMap) => {
      Object.entries(sel).forEach(([key, selNode]) => {
        const optNode = opt[key];
        if (!optNode) return;

        const optChildren = optNode.children || {};
        const selChildren = selNode.children || {};

        if (Object.keys(optChildren).length > 0) {
          // Recurse first
          traverse(optChildren, selChildren);

          const total = Object.keys(optChildren).length;
          const selectedKeys = Object.keys(selChildren);

          const allChildrenSelected =
            selectedKeys.length === total &&
            selectedKeys.every((k) => selChildren[k]?.isSelected);

          const atLeastSomeChildrenSelected = Object.values(selChildren).some(
            (c) => c.isPartialSelected || c.isSelected,
          );

          selNode.isSelected = allChildrenSelected;
          selNode.isPartialSelected = !allChildrenSelected && atLeastSomeChildrenSelected;
        } else {
          // Leaf node — if present, mark as fully selected
          selNode.isSelected = true;
          selNode.isPartialSelected = false;
        }
      });
    };

    traverse(options, draft);
  });
}

/**
 * Get children for a specific level based on keys.
 * @param keys - Array of keys representing the path to the desired level.
 * @param struct - The data structure to traverse.
 * @returns The SelectionTreeNode at the specified level, or undefined if not found.
 * */
export function getChildrenForLevel(
  keys: (string | number)[],
  struct: DataMap,
): SelectionTreeNode | undefined {
  let current: TreeNode | undefined;

  keys.forEach((key) => {
    if (!current) {
      const children = struct;
      const next = children[key];
      if (!next) return;
      current = next;
    } else {
      if (!current.children) return;
      const next = current.children[key];
      if (!next) {
        current = undefined;

        return;
      }
      current = next;
    }
  });

  if (!current) return undefined;

  return current;
}

/**
 * Find deepest children (leaf nodes) of a given node.
 * @param node - The tree node to search.
 * @returns A record of the deepest children nodes, or undefined if none exist.
 */
function findDeepestChildren(
  node: TreeNode,
): Record<string | number, TreeNode> | undefined {
  if (!node.children) return undefined;

  const childValues = Object.values(node.children);
  const hasDeeper = childValues.some((c) => c.children);

  if (!hasDeeper) {
    return node.children; // leaf-level children
  }

  const deepest: Record<string | number, TreeNode> = {};
  childValues.forEach((child) => {
    const childDeep = findDeepestChildren(child);
    if (childDeep) {
      Object.keys(childDeep).forEach((k) => {
        deepest[k] = childDeep[k];
      });
    }
  });

  return deepest;
}

/**
 * Flatten selection tree to array of selected ids with categories and statuses.
 *
 * Each entry in the result array has the structure:
 * {
 *   id: number; // document type id
 *   categories: number[]; // selected category ids
 *   statuses: number[]; // selected status ids
 * }
 *
 * @param tree - The selection tree to flatten.
 * @returns An array of selected document types with their categories and statuses.
 */
export function flattenTreeToArray(tree: SelectionMap) {
  const result: { id: number; categories: number[]; statuses: number[] }[] = [];

  Object.entries(tree).forEach(([id, node]) => {
    const categories: number[] = [];
    const statuses: number[] = [];
    if (node.isSelected === false && node.isPartialSelected === false) {
      return; // skip unselected nodes
    }

    if (node.children) {
      // iterate through category and status children
      Object.entries(node.children).forEach(([childKey, childNode]) => {
        if (childKey.startsWith('categories-') && childNode.children) {
          const leaves = findDeepestChildren(childNode);
          if (leaves && !isEmptyObject(leaves)) {
            categories.push(...Object.keys(leaves).map(Number));
          }
        }
        if (childKey.startsWith('statuses-') && childNode.children) {
          const leaves = findDeepestChildren(childNode);
          if (leaves && !isEmptyObject(leaves)) {
            statuses.push(...Object.keys(leaves).map(Number));
          }
        }
      });
    }

    if (categories.length === 0 && statuses.length === 0) {
      return; // skip if nothing selected
    }

    result.push({
      id: Number(id),
      categories,
      statuses,
    });
  });

  return result;
}

/**
 * Get an empty object without children at the bottom level (ie: prune the leaves).
 * @param struct - The data structure to prune.
 * @returns A new data structure with leaves pruned.
 */
export function getEmptyObject(struct: DataMap): DataMap {
  const result: DataMap = {};

  Object.keys(struct).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(struct, key)) return;

    const node = struct[key];
    const newNode: TreeNode = { label: node.label, value: node.value };

    if (node.children) {
      const childValues = Object.values(node.children);
      const hasDeeper = childValues.some((c) => c.children);
      if (hasDeeper) {
        newNode.children = getEmptyObject(node.children);
      } else {
        newNode.children = {};
      }
    }

    result[key] = newNode;
  });

  return result;
}
